import React from "react";
import ClinicIcon from "./ClinicIcon";
import InitialsAvatar from "./InitialsAvatar";
import "./ActivePatientBar.css";

interface ActivePatientBarProps {
    patient: {
        patient_id?: string | number;
        name?: string;
        age?: string | number;
        gender?: string;
    } | null;
    visit: {
        visit_id?: string | number;
        status?: string;
    } | null;
    loadingVisit?: boolean;
    onEndSession?: () => void;
    onEdit?: () => void;
    accentColor?: string; // CSS var passthrough
    className?: string;
}

function normalizeStatus(raw?: unknown) {
    return String(raw ?? "").trim().toUpperCase().replace(/\s+/g, "_");
}

function getStatusClass(raw?: unknown): string {
    const s = normalizeStatus(raw);
    if (["IN_CHAIR", "IN_PROGRESS"].includes(s)) return "status-in-chair";
    if (["OPEN", "CHECKED_IN", "BOOKED", "CONFIRMED"].includes(s)) return "status-open";
    if (["WAITING"].includes(s)) return "status-waiting";
    if (["LOCKED", "COMPLETED", "CLOSED", "DONE"].includes(s)) return "status-locked";
    return "status-open";
}

function getStatusLabel(raw?: unknown): string {
    const s = normalizeStatus(raw);
    const MAP: Record<string, string> = {
        IN_CHAIR: "In Chair",
        IN_PROGRESS: "In Progress",
        OPEN: "Open",
        CHECKED_IN: "Checked In",
        WAITING: "Waiting",
        BOOKED: "Booked",
        CONFIRMED: "Confirmed",
        LOCKED: "Locked",
        COMPLETED: "Completed",
        CLOSED: "Closed",
        DONE: "Done",
    };
    return MAP[s] ?? s ?? "—";
}

export default function ActivePatientBar({
    patient,
    visit,
    loadingVisit = false,
    onEndSession,
    onEdit,
    className = "",
}: ActivePatientBarProps) {
    if (!patient) return null;

    const name = patient.name || "Unknown Patient";
    const agePart = patient.age ? `${patient.age} yrs` : null;
    const genderPart = patient.gender
        ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1, 4).toLowerCase()
        : null;
    const meta = [agePart, genderPart, `#${patient.patient_id}`].filter(Boolean).join("  ·  ");

    const visitId = visit?.visit_id;
    const statusClass = loadingVisit ? "status-loading" : getStatusClass(visit?.status);
    const statusLabel = loadingVisit ? "Loading..." : getStatusLabel(visit?.status);

    return (
        <div className={`apb-root ${className}`} role="status" aria-live="polite">
            {/* Avatar */}
            <div className="apb-avatar-wrapper" style={{ flexShrink: 0 }}>
                <InitialsAvatar name={name} size={32} />
            </div>

            {/* Info */}
            <div className="apb-info">
                <span className="apb-name">{name}</span>
                <span className="apb-meta">
                    {meta}
                    {onEdit && (
                        <button
                            type="button"
                            className="apb-edit-inline-btn"
                            onClick={onEdit}
                            title="Edit patient details"
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#0ea5e9',
                                marginLeft: '8px',
                                padding: '2px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                verticalAlign: 'middle'
                            }}
                        >
                            <ClinicIcon name="edit" size={14} />
                        </button>
                    )}
                </span>
            </div>

            {/* Visit badge */}
            <div className="apb-visit">
                {visitId && (
                    <span className="apb-visit-id">Visit #{visitId}</span>
                )}
                <span className={`apb-status-chip ${statusClass}`}>
                    <span className="apb-status-dot" />
                    {statusLabel}
                </span>
            </div>

            {/* End session */}
            {onEndSession && (
                <button
                    type="button"
                    className="apb-end-btn"
                    onClick={onEndSession}
                    title="End session and clear patient"
                    aria-label="End patient session"
                >
                    <ClinicIcon name="close" size={12} />
                    <span>End Session</span>
                </button>
            )}
        </div>
    );
}
